import { Routes } from '@angular/router';
import { authGuard } from '@/app/core/guards/auth.guard';
import { platformAdminGuard } from '@/app/core/guards/platform-admin.guard';
import { redireccionesPermanentes } from './rutas-canonicas';

/**
 * **Ningún componente de pantalla se importa acá arriba** (Historia 1.13 · AD-17).
 *
 * Hasta este commit había cinco importados estáticamente —las tres de autenticación, el layout
 * y la pantalla de «en construcción»— contra 22 `loadChildren` y 4 `loadComponent` en el mismo
 * archivo. Un `import` en la cabeza de este archivo es código que entra al bundle inicial, o sea
 * que **todo el mundo lo descarga antes de ver nada**, lo pida o no.
 *
 * Las tres de autenticación son el caso más claro: un usuario con sesión nunca las pide, y son
 * justamente las que traen consigo los formularios y sus validadores.
 *
 * `MainLayoutComponent` podría ser la excepción justificada —quien entra con sesión lo pide
 * siempre— pero tampoco hace falta: el router lo resuelve antes del primer render y la ganancia
 * de tenerlo eager es un viaje que el navegador hace igual. Si algún día se justifica, se
 * escribe acá el motivo; el test de la 1.13 lee este archivo y exige cero componentes.
 */

export const routes: Routes = [
  {
    path: 'sign-in',
    loadComponent: () =>
      import('@/app/features/auth/pages/sign-in-page/sign-in-page.component').then(
        (m) => m.SignInPageComponent,
      ),
  },
  {
    path: 'sign-up',
    loadComponent: () =>
      import('@/app/features/auth/pages/sign-up-page/sign-up-page.component').then(
        (m) => m.SignUpPageComponent,
      ),
  },
  {
    path: 'invitations/accept',
    loadComponent: () =>
      import('@/app/features/auth/pages/accept-invitation-page/accept-invitation-page.component').then(
        (m) => m.AcceptInvitationPageComponent,
      ),
  },
  {
    // Públicas: quien perdió la contraseña, por definición, no tiene sesión.
    // La ruta 'password/reset' está horneada en el enlace del correo
    // (graph_mailer.send_password_reset_email): cambiarla de un solo lado
    // rompería todos los enlaces ya enviados.
    path: 'password/forgot',
    loadComponent: () =>
      import('@/app/features/auth/pages/forgot-password-page/forgot-password-page.component').then(
        (m) => m.ForgotPasswordPageComponent,
      ),
  },
  {
    path: 'password/reset',
    loadComponent: () =>
      import('@/app/features/auth/pages/reset-password-page/reset-password-page.component').then(
        (m) => m.ResetPasswordPageComponent,
      ),
  },
  {
    // Public, like the reset: the email change link (core H119) reaches the new mailbox, often on
    // another device and without a session. The path is baked into the link the backend mails
    // (graph_mailer.send_email_change_confirmation_email): renaming it on one side breaks every
    // link already sent.
    path: 'auth/confirmar-email',
    loadComponent: () =>
      import('@/app/features/auth/pages/confirm-email-page/confirm-email-page.component').then(
        (m) => m.ConfirmEmailPageComponent,
      ),
  },
  {
    path: 'under-construction',
    loadComponent: () =>
      import('@/app/shared/ui/under-construction/under-construction-page.component').then(
        (m) => m.UnderConstructionPageComponent,
      ),
  },
  {
    // Public landing for Instagram's OAuth redirect (runs inside the login popup;
    // posts the code back to the opener). Must stay outside the auth guard.
    path: 'connect/instagram/callback',
    loadComponent: () =>
      import('@/app/features/channels/pages/instagram-callback/instagram-callback.component').then(
        (m) => m.InstagramCallbackComponent,
      ),
  },
  {
    // Public landing for Facebook Messenger's OAuth redirect (same popup pattern
    // as Instagram). Must stay outside the auth guard.
    path: 'connect/messenger/callback',
    loadComponent: () =>
      import('@/app/features/channels/pages/messenger-callback/messenger-callback.component').then(
        (m) => m.MessengerCallbackComponent,
      ),
  },
  {
    // La página del sistema de diseño (FR-5, Historia 1.4). Cuelga de la raíz, fuera del
    // `MainLayoutComponent` y de su `authGuard`, porque tiene que funcionar sin backend y
    // sin sesión: es la superficie con la que se verifica el sistema el día que lo que está
    // roto es justamente el login. Ruta en castellano (AD-14); no hay ruta vieja que redirigir.
    path: 'sistema-de-diseno',
    loadChildren: () => import('@/app/design/kit/kit.routes').then((m) => m.kitRoutes),
  },
  {
    path: '',
    loadComponent: () =>
      import('@/app/core/layouts/main-layout/main-layout.component').then(
        (m) => m.MainLayoutComponent,
      ),
    canActivate: [authGuard],
    children: [
      {
        // El panel v2 (1.6a/b/c). Cargaba `dashboardRoutes` —el panel viejo—
        // aunque `PanelPageComponent` estuviera construido y probado: cambiar
        // el armazón sin cambiar lo de adentro habría dejado la navegación
        // nueva alrededor de la pantalla vieja.
        path: 'panel',
        loadChildren: () => import('@/app/features/panel/panel.routes').then((m) => m.panelRoutes),
      },
      {
        path: 'agentes',
        loadChildren: () =>
          import('@/app/features/agents/agents.routes').then((m) => m.agentsRoutes),
      },
      {
        path: 'configuracion',
        loadChildren: () =>
          import('@/app/features/settings/settings.routes').then((m) => m.settingsRoutes),
      },
      {
        // Uso & Facturación (#view-facturacion de la maqueta), con ruta propia. Era la pestaña
        // `?tab=facturacion` de Configuración, que ahora redirige acá (`settings.routes.ts`).
        path: 'facturacion',
        loadChildren: () =>
          import('@/app/features/settings/facturacion.routes').then((m) => m.facturacionRoutes),
      },
      {
        // Vista CONTEXTUAL: no va en `nav.model.ts`. Se llega desde el avatar
        // del topbar, no desde la navegación principal (Historia 8.4).
        path: 'perfil',
        loadChildren: () =>
          import('@/app/features/profile/profile.routes').then((m) => m.profileRoutes),
      },
      {
        path: 'canales',
        loadChildren: () =>
          import('@/app/features/channels/channels.routes').then((m) => m.channelsRoutes),
      },
      {
        path: 'conversaciones',
        loadChildren: () =>
          import('@/app/features/conversations/conversations.routes').then(
            (m) => m.conversationsRoutes,
          ),
      },
      {
        path: 'leads',
        loadChildren: () => import('@/app/features/leads/leads.routes').then((m) => m.leadsRoutes),
      },
      {
        path: 'calendario',
        loadChildren: () =>
          import('@/app/features/calendario/calendario.routes').then((m) => m.calendarioRoutes),
      },
      {
        path: 'base-conocimiento',
        loadChildren: () =>
          import('@/app/features/knowledge-base/knowledge-base.routes').then(
            (m) => m.knowledgeBaseRoutes,
          ),
      },
      {
        path: 'integraciones',
        loadChildren: () =>
          import('@/app/features/integrations/integrations.routes').then(
            (m) => m.integrationsRoutes,
          ),
      },
      {
        // La ruta se registra SIEMPRE y la compuerta la resuelve la pantalla:
        // el criterio 5 de la 11.12 pide que el acceso directo con el flag
        // apagado muestre «capacidad no activada», no un error crudo ni una
        // redirección a otro lado. Quien decide es el servidor, con su 403.
        path: 'insights',
        loadChildren: () =>
          import('@/app/features/insights/insights.routes').then((m) => m.insightsRoutes),
      },
      {
        path: 'laboratorio',
        loadChildren: () => import('@/app/features/labs/labs.routes').then((m) => m.labsRoutes),
      },
      {
        path: 'analitica',
        loadChildren: () =>
          import('@/app/features/analytics/analytics.routes').then((m) => m.analyticsRoutes),
      },
      {
        path: 'recursos',
        loadChildren: () =>
          import('@/app/features/recursos/recursos.routes').then((m) => m.recursosRoutes),
      },
      {
        path: 'catalogo',
        loadChildren: () =>
          import('@/app/features/catalogo/catalogo.routes').then((m) => m.catalogoRoutes),
      },
      {
        path: 'equipo',
        loadChildren: () => import('@/app/features/team/team.routes').then((m) => m.teamRoutes),
      },
      {
        path: 'soporte',
        loadChildren: () =>
          import('@/app/features/support/support.routes').then((m) => m.supportRoutes),
      },
      {
        path: 'impersonacion',
        loadChildren: () =>
          import('@/app/features/impersonation/impersonation.routes').then(
            (m) => m.impersonationRoutes,
          ),
      },
      {
        path: 'admin',
        loadChildren: () => import('@/app/features/admin/admin.routes').then((m) => m.adminRoutes),
        canActivate: [platformAdminGuard],
      },
      {
        // Estudio de Clon (RTA-02). The add-on gate is server-side (403);
        // the old sidebar (tag `consola-v1-final`) hid it for tenants without the add-on.
        path: 'clone-studio',
        loadChildren: () =>
          import('@/app/features/persona/persona.routes').then((m) => m.personaRoutes),
      },
      {
        // Voz y Avatar (RTA-03). Same gating model as clone-studio:
        // server-side 403; the old sidebar (tag `consola-v1-final`) hid it without the add-on.
        path: 'voice-avatar',
        loadChildren: () =>
          import('@/app/features/voice-avatar/voice-avatar.routes').then(
            (m) => m.voiceAvatarRoutes,
          ),
      },
      {
        path: 'notificaciones',
        loadChildren: () =>
          import('@/app/features/notifications/notifications.routes').then(
            (m) => m.notificationsRoutes,
          ),
      },
      {
        path: 'pagos',
        loadChildren: () => import('@/app/features/pagos/pagos.routes').then((m) => m.pagosRoutes),
      },
      // ─── Las 15 redirecciones permanentes de AD-14 ────────────────────────────────
      // Cada ruta vieja sigue existiendo y aterriza en su canónica en español. La lista
      // se cuenta en `rutas-canonicas.ts`, que también explica por qué `leads` y `admin`
      // no se renombran y por qué las seis rutas que salen del nav (Historia 1.8) tienen
      // su path congelado.
      //
      // Van DESPUÉS de las canónicas y ANTES del comodín. El orden entre ellas lo fija
      // `redireccionesPermanentes()` —más segmentos primero—, porque el router toma el
      // primer path que matchea y `conversations` se comería `conversations/assignments`.
      ...redireccionesPermanentes(),

      {
        path: '',
        redirectTo: '/panel',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: '**',
    redirectTo: '/sign-in',
  },
];
